/**
 * Wizard card for the conversion_labels step (the bridge between ads_conversions
 * and verify_ads_active).
 *
 * Google populates a conversion action's tag snippet (which carries the
 * `AW-xxx/LABEL` conversion label) ASYNCHRONOUSLY after the action is created —
 * so capturing labels can't be done reliably in the same run that creates the
 * actions. This step re-queries the actions and writes the five `gads_label_*`
 * settings the viewer reads to fire each funnel conversion. It is re-runnable:
 * if Google hasn't populated the snippets yet the step is `waiting_on_vendor`
 * and the operator clicks Refresh a few minutes later.
 *
 * No secret here — the Ads credentials are platform env; labels are identifiers.
 * Values read back from account_settings so the table survives a page refresh.
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, Circle, Loader2, RefreshCw, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { supabase } from '@/lib/supabase'
import type { OnboardingTask } from '@limo/database'
import { useInvokeOnboardingStep } from '@/hooks/useOnboarding'
import { useToast } from '@/hooks/use-toast'
import { StepCard } from './StepCard'
import { GoogleAdsAccountBadge } from './GoogleAdsAccountBadge'

interface ConversionLabelsCardProps {
  task: OnboardingTask
  accountId: string
  runId: string
}

interface LabelResult {
  name: string
  label: string | null
}

interface ConversionLabelsResult {
  labels?: LabelResult[]
  captured?: number
  total?: number
}

/** The five funnel events the viewer wires, in display order, with friendly names. */
const LABEL_EVENTS: ReadonlyArray<{ name: string; display: string }> = [
  { name: 'get_vehicle_quotes', display: 'Get Vehicle Quotes' },
  { name: 'select_vehicle', display: 'Select Vehicle' },
  { name: 'provide_contact_info', display: 'Provide Contact Info' },
  { name: 'purchase', display: 'Purchase' },
  { name: 'generate_lead', display: 'Generate Lead' },
]

export function ConversionLabelsCard({
  task,
  accountId,
  runId,
}: ConversionLabelsCardProps) {
  const { toast } = useToast()
  const invokeStep = useInvokeOnboardingStep(runId)

  const result = (task.result ?? {}) as ConversionLabelsResult
  const hasRun = task.status !== 'pending'

  // Read back saved labels so the table shows after a page refresh (none secret).
  const { data: saved } = useQuery({
    queryKey: ['onboarding-conversion-labels', accountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('account_settings')
        .select('key, value')
        .eq('account_id', accountId)
        .in(
          'key',
          LABEL_EVENTS.map((e) => `gads_label_${e.name}`)
        )
      if (error) throw new Error(error.message)
      const map: Record<string, string> = {}
      for (const row of data ?? []) {
        if (row.value != null) map[row.key] = row.value
      }
      return map
    },
  })

  // Prefer the run result; fall back to saved settings.
  const rows = useMemo(() => {
    const byName = new Map((result.labels ?? []).map((l) => [l.name, l.label]))
    return LABEL_EVENTS.map((e) => ({
      ...e,
      label: byName.get(e.name) ?? saved?.[`gads_label_${e.name}`] ?? null,
    }))
  }, [result.labels, saved])

  const captured = rows.filter((r) => r.label).length

  const syncLabels = () =>
    invokeStep.mutate(
      {
        functionName: 'onboard-google-ads',
        accountId,
        params: { action: 'sync_labels' },
      },
      {
        onError: (error) =>
          toast({
            title: 'Conversion label sync failed',
            description: error.message,
            variant: 'destructive',
          }),
      }
    )

  return (
    <StepCard
      task={task}
      manualFallback={
        <div className="space-y-2">
          <p>
            Conversion labels are generated by Google a few minutes after the
            conversion actions are created. If sync keeps coming back empty, the
            client can paste them manually under Settings &gt; Conversion Labels.
          </p>
          <p>
            Find each label in Google Ads &gt; Goals &gt; Conversions &gt;
            [Conversion] &gt; Tag setup &gt; Use Google Tag Manager — it&rsquo;s
            the alphanumeric string after the slash in <code>AW-xxx/LABEL</code>.
          </p>
          <p>
            Prerequisite: the <code>ads_conversions</code> step must have run
            (it creates the actions and stores the customer ID).
          </p>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        <GoogleAdsAccountBadge accountId={accountId} />
        {hasRun && rows.some((r) => r.label) ? (
          <>
            <div className="overflow-hidden rounded-md border">
              <table className="w-full text-xs">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-2 py-1 text-left font-medium">Event</th>
                    <th className="px-2 py-1 text-left font-medium">Label</th>
                    <th className="px-2 py-1 text-left font-medium">Captured</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((r) => (
                    <tr key={r.name}>
                      <td className="px-2 py-1">{r.display}</td>
                      <td className="px-2 py-1 font-mono">{r.label ?? '—'}</td>
                      <td className="px-2 py-1">
                        {r.label ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : (
                          <Circle className="h-4 w-4 text-muted-foreground" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant={captured === rows.length ? 'outline' : 'default'}
                onClick={syncLabels}
                disabled={invokeStep.isPending}
              >
                {invokeStep.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Refresh
              </Button>
              <span className="text-xs text-muted-foreground">
                {captured}/{rows.length} captured
              </span>
            </div>
            {captured < rows.length && (
              <p className="text-xs text-amber-700">
                Google can take a few minutes to publish the tag snippets — click
                Refresh again shortly.
              </p>
            )}
          </>
        ) : (
          <div className="space-y-2">
            <p className="text-muted-foreground">
              Captures the five conversion labels (<code>gads_label_*</code>) the
              viewer fires for each funnel event. Run after the Google Ads
              conversions step.
            </p>
            <Button
              type="button"
              size="sm"
              onClick={syncLabels}
              disabled={invokeStep.isPending}
            >
              {invokeStep.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              Capture labels
            </Button>
          </div>
        )}
      </div>
    </StepCard>
  )
}
